import { useCallback, useEffect } from "react";
import { matchDevice, rememberDevice } from "@/engine/media/deviceChoice";
import { getSettings, setSetting } from "@/engine/state/settings";
import { useSettings } from "@/hooks/useSettings";
import { useMediaDevices, type MediaDevice } from "@/hooks/useMediaDevices";

/**
 * Which microphone and camera this visitor is on, and why.
 *
 * One place decides it, and that is the point. The room used to seed both
 * from the system default inside the settings panel, so the decision was made
 * only if the visitor opened the panel — and the panel could not tell a default
 * it had just applied from a device the visitor had pressed. This hook is
 * mounted with the room instead, and the panel's handlers become what they read
 * as: somebody chose this.
 *
 * Three rules, in order, per device:
 *
 * - A remembered device that is present is selected, whenever it appears. That
 *   is what returns somebody to their headset when they plug it back in.
 * - A remembered device that is absent stays remembered. A present device
 *   carries them meanwhile and is **not** written back as a choice: absence is
 *   the hardware's statement, not the visitor's.
 * - A visitor who has never chosen follows the system default, and keeps
 *   following it. Never having chosen is not recorded as a choice.
 *
 * Nothing is matched while the browser is withholding device names: an
 * enumeration that names nothing is not evidence the remembered device is gone,
 * and matching against one would discard a correct choice on every load that
 * beat the permission.
 *
 * There is no speaker here: the room plays through the system's output, which
 * the visitor changes in their operating system.
 */
export interface RememberedDevices {
  audioInputs: MediaDevice[];
  videoInputs: MediaDevice[];
  defaultAudioInput?: string;
  defaultVideoInput?: string;
  isLoaded: boolean;
  /** Whether the browser names its devices yet; see `useMediaDevices`. */
  namesReadable: boolean;
  /** Asks the browser for its devices again — after a grant, say. */
  refresh: () => Promise<void>;
  /** Pass these to the panel: each is a visitor's press, and is remembered. */
  rememberAudioInput: (deviceId: string) => void;
  rememberCamera: (deviceId: string) => void;
}

export interface RememberedDevicesOptions {
  selectedAudioInput: string | null;
  selectedCamera: string | null;
  onAudioInputChange: (deviceId: string) => void;
  onCameraChange: (deviceId: string) => void;
}

/** One device's worth of the three rules above. */
function resolve(
  choice: Parameters<typeof matchDevice>[0],
  devices: readonly MediaDevice[],
  namesReadable: boolean,
  selected: string | null,
  systemDefault: string | undefined,
  select: (deviceId: string) => void,
) {
  const remembered = namesReadable ? matchDevice(choice, devices) : null;
  if (remembered) {
    if (remembered !== selected) select(remembered);
    return;
  }
  // Either nothing was ever chosen, or what was chosen is not here. Both are
  // carried by the system's own default, and neither is written back.
  if (!selected && systemDefault) select(systemDefault);
}

export function useRememberedDevices({
  selectedAudioInput,
  selectedCamera,
  onAudioInputChange,
  onCameraChange,
}: RememberedDevicesOptions): RememberedDevices {
  const {
    audioInputs,
    videoInputs,
    defaultAudioInput,
    defaultVideoInput,
    isLoaded,
    namesReadable,
    refresh,
  } = useMediaDevices();
  const { audioInput, camera } = useSettings();

  useEffect(() => {
    if (!isLoaded) return;
    resolve(
      audioInput,
      audioInputs,
      namesReadable,
      selectedAudioInput,
      defaultAudioInput,
      onAudioInputChange,
    );
    resolve(
      camera,
      videoInputs,
      namesReadable,
      selectedCamera,
      defaultVideoInput,
      onCameraChange,
    );
  }, [
    isLoaded,
    namesReadable,
    audioInput,
    camera,
    audioInputs,
    videoInputs,
    selectedAudioInput,
    selectedCamera,
    defaultAudioInput,
    defaultVideoInput,
    onAudioInputChange,
    onCameraChange,
  ]);

  /**
   * A press: select it, and remember it as this machine's choice. The store is
   * read through `getSettings` rather than the snapshot above so that two
   * presses in one render cannot write the first one twice.
   */
  const press = useCallback(
    (
      key: "audioInput" | "camera",
      devices: readonly MediaDevice[],
      select: (deviceId: string) => void,
    ) =>
      (deviceId: string) => {
        select(deviceId);
        const choice = rememberDevice(deviceId, devices);
        if (choice && getSettings()[key]?.deviceId !== choice.deviceId)
          setSetting(key, choice);
      },
    [],
  );

  return {
    audioInputs,
    videoInputs,
    defaultAudioInput,
    defaultVideoInput,
    isLoaded,
    namesReadable,
    refresh,
    rememberAudioInput: press("audioInput", audioInputs, onAudioInputChange),
    rememberCamera: press("camera", videoInputs, onCameraChange),
  };
}
